// ── cohort-attendance-collection: weekly rollup adapter for attendance tab ──

import { useMemo } from "react";
import { Badge, StatusDot, useCollection, type CollectionColumnDef, type UseCollectionResult } from "@vjal/ui";

import type { CoachingAttendanceWeek } from "../../../services/coaching-z6-dtos";
import { COHORT_TAB_PAGE_SIZE } from "./cohort-tab-collection-shared";

export type AttendanceRow = {
  id: string;
  week: string;
  session: string;
  date: string;
  attended: string;
  missed: string;
  ingested: string;
} & Record<string, unknown>;

export function mapAttendanceWeeks(weeks: CoachingAttendanceWeek[]): AttendanceRow[] {
  return weeks.map((week) => ({
    id: String(week.weekNumber),
    week: `Week ${week.weekNumber}`,
    session: week.title ?? `Session ${week.weekNumber}`,
    date: week.sessionDate ?? "—",
    attended: `${week.attendedCount} attended`,
    missed: `${week.missedCount} missed`,
    ingested: week.ingested ? "recorded" : "awaiting Zoom data"
  }));
}

export function buildAttendanceCollectionColumns(): CollectionColumnDef<AttendanceRow>[] {
  return [
    { id: "week", header: "Week", priority: 1, cell: (row) => row.week },
    {
      id: "session",
      header: "Session",
      searchValue: (row) => `${row.session} ${row.date}`,
      priority: 1,
      cell: (row) => row.session
    },
    { id: "date", header: "Date", priority: 2, cell: (row) => row.date },
    {
      id: "attended",
      header: "Attended",
      priority: 2,
      cell: (row) => <Badge variant="success">{row.attended}</Badge>
    },
    {
      id: "missed",
      header: "Missed",
      priority: 2,
      cell: (row) => <Badge variant="warning">{row.missed}</Badge>
    },
    {
      id: "ingested",
      header: "Source",
      priority: 3,
      cell: (row) => (
        <StatusDot tone={row.ingested === "recorded" ? "ok" : "info"} label={row.ingested} />
      )
    }
  ];
}

export function useCohortAttendanceCollection(rows: AttendanceRow[]): UseCollectionResult<AttendanceRow> {
  const columns = useMemo(() => buildAttendanceCollectionColumns(), []);

  return useCollection<AttendanceRow>({
    mode: "client",
    rows,
    columns,
    pageSize: COHORT_TAB_PAGE_SIZE,
    urlSync: false,
    getRowKey: (row) => row.id
  });
}
